import React, { useState, useEffect } from 'react';
import { useModuleSettings } from '../../contexts/ModuleSettingsContext';
import { getModuleSettings, updateModuleSettings } from '../../services/moduleSettingsService';
import Toast from '../../components/Common/Toast';

const ModuleSettings = () => {
  const {
    machineryEnabled,
    setMachineryEnabled,
    produccionEnabled,
    setProduccionEnabled,
    produccionConcesionariaEnabled,
    setProduccionConcesionariaEnabled,
    produccionFabricarPepDesdePtEnabled,
    setProduccionFabricarPepDesdePtEnabled,
    produccionLineasEnabled,
    setProduccionLineasEnabled,
    restaurantesEnabled,
    setRestaurantesEnabled,
    activosEnabled,
    setActivosEnabled,
    ventasEnabled,
    setVentasEnabled,
    ventasCashLiquidationEnabled,
    setVentasCashLiquidationEnabled,
    kardexAuditModuleEnabled,
    setKardexAuditModuleEnabled,
    loaded,
  } = useModuleSettings();
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [ventasPosAssignment, setVentasPosAssignment] = useState(true);
  const [ventasPriceRanges, setVentasPriceRanges] = useState(false);
  const [ventasOffers, setVentasOffers] = useState(true);

  useEffect(() => {
    if (!ventasEnabled) return;
    getModuleSettings().then((res) => {
      if (res.success && res.data) {
        setVentasPosAssignment(res.data.ventas_pos_product_assignment_enabled !== false);
        setVentasPriceRanges(Boolean(res.data.ventas_price_ranges_enabled));
        setVentasOffers(res.data.ventas_offers_enabled !== false);
      }
    });
  }, [ventasEnabled, loaded]);

  const runToggle = (promise) => {
    if (promise && typeof promise.then === 'function') {
      promise
        .then(() => setToast({ isOpen: true, message: 'Configuración guardada', type: 'success' }))
        .catch(() => setToast({ isOpen: true, message: 'No se pudo guardar la configuración', type: 'error' }));
    }
  };

  const runVentasOptionToggle = (key, value, setter) => {
    setter(value);
    updateModuleSettings({ [key]: value })
      .then((res) => {
        if (res.success) {
          setToast({ isOpen: true, message: 'Configuración guardada', type: 'success' });
        }
      })
      .catch(() => {
        setter(!value);
        setToast({ isOpen: true, message: 'No se pudo guardar la configuración', type: 'error' });
      });
  };

  const modules = [
    {
      id: 'warehouses',
      name: 'Almacenes',
      description: 'Almacenes, reportes, kardex, entradas y salidas.',
      icon: '🏭',
      alwaysActive: true,
    },
    {
      id: 'purchases',
      name: 'Compras',
      description: 'Proveedores, órdenes de compra y servicio, documentos.',
      icon: '🛒',
      alwaysActive: true,
    },
    {
      id: 'machinery',
      name: 'Maquinaria',
      description: 'Máquinas, vehículos, horómetros, kilómetros y mantenimientos.',
      icon: '🚜',
      alwaysActive: false,
      enabled: machineryEnabled,
      onToggle: (v) => runToggle(setMachineryEnabled(v)),
    },
    {
      id: 'produccion',
      name: 'Producción',
      description: 'Catálogo general: clasificación de recetas y fichas técnicas.',
      icon: '🏭',
      alwaysActive: false,
      enabled: produccionEnabled,
      onToggle: (v) => runToggle(setProduccionEnabled(v)),
    },
    {
      id: 'produccion-concesionaria',
      name: 'Producción concesionaria',
      description: 'Planeamiento cíclico, salidas a producción y despacho en planta concesionaria.',
      icon: '🏢',
      alwaysActive: false,
      enabled: produccionConcesionariaEnabled,
      onToggle: (v) => runToggle(setProduccionConcesionariaEnabled(v)),
    },
    {
      id: 'restaurantes',
      name: 'Restaurantes',
      description: 'Operaciones (pedidos, mesas, producción), recetas y configuración de cocinas.',
      icon: '🍴',
      alwaysActive: false,
      enabled: restaurantesEnabled,
      onToggle: (v) => runToggle(setRestaurantesEnabled(v)),
    },
    {
      id: 'activos',
      name: 'Activos',
      description: 'Registro de activos con clasificación propia y ubicación por área de empresa.',
      icon: '📦',
      alwaysActive: false,
      enabled: activosEnabled,
      onToggle: (v) => runToggle(setActivosEnabled(v)),
    },
    {
      id: 'ventas',
      name: 'Ventas',
      description: 'Clientes, cotizaciones, ventas, lista de precios y puntos de venta.',
      icon: '💰',
      alwaysActive: false,
      enabled: ventasEnabled,
      onToggle: (v) => runToggle(setVentasEnabled(v)),
    },
    {
      id: 'kardex-audit',
      name: 'Auditoría kardex e inventario',
      description: 'Alertas de kardex, traspasos y toma de inventario por almacén (desactivado por defecto).',
      icon: '🔍',
      alwaysActive: false,
      enabled: kardexAuditModuleEnabled,
      onToggle: (v) => runToggle(setKardexAuditModuleEnabled(v)),
    },
  ];

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">Habilitación de Módulos</h1>
        <p className="mt-1 text-sm text-gray-500">
          Los módulos Almacenes y Compras están siempre activos. Puede activar o desactivar Maquinaria, Producción,
          Producción concesionaria, Restaurantes, Activos, Ventas y Auditoría kardex e inventario (aparece en el menú lateral como «Auditoría»).
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <ul className="divide-y divide-gray-200">
          {modules.map((mod) => (
            <li key={mod.id} className="px-6 py-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-4 min-w-0">
                <span className="text-2xl flex-shrink-0" aria-hidden>{mod.icon}</span>
                <div className="min-w-0">
                  <p className="font-medium text-gray-900">{mod.name}</p>
                  <p className="text-sm text-gray-500">{mod.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                {mod.alwaysActive ? (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-teal-100 text-teal-800">
                    Siempre activo
                  </span>
                ) : (
                  <>
                    <span className="text-sm text-gray-500">
                      {mod.enabled ? 'Activo' : 'Inactivo'}
                    </span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={mod.enabled}
                      onClick={() => mod.onToggle(!mod.enabled)}
                      disabled={!loaded}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 ${
                        mod.enabled ? 'bg-teal-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          mod.enabled ? 'translate-x-5' : 'translate-x-1'
                        }`}
                        aria-hidden
                      />
                    </button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      {ventasEnabled && (
        <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-medium text-gray-800">Opciones de Ventas</h2>
            <p className="text-sm text-gray-500 mt-1">
              Lista de precios por punto de venta, catálogo habilitado, ofertas y liquidación de caja.
            </p>
          </div>
          <ul className="divide-y divide-gray-200">
            {[
              {
                id: 'pos_assignment',
                label: 'Configuración de productos por punto de venta',
                description:
                  'Permite habilitar qué productos se venden en cada POS (pestaña Configuración en lista de precios).',
                enabled: ventasPosAssignment,
                key: 'ventas_pos_product_assignment_enabled',
                setter: setVentasPosAssignment,
              },
              {
                id: 'price_ranges',
                label: 'Rangos de precio (mínimo / máximo)',
                description: 'Muestra columnas de precio mínimo y máximo por producto y POS.',
                enabled: ventasPriceRanges,
                key: 'ventas_price_ranges_enabled',
                setter: setVentasPriceRanges,
              },
              {
                id: 'offers',
                label: 'Generador de ofertas',
                description: 'Habilita la pestaña Ofertas dentro de Lista de precios (promociones por POS).',
                enabled: ventasOffers,
                key: 'ventas_offers_enabled',
                setter: setVentasOffers,
              },
              {
                id: 'cash_liquidation',
                label: 'Liquidación de caja',
                description:
                  'Habilita el menú Liquidación de caja. Si está desactivado, no se consulta ni se guarda nada en caja.',
                enabled: ventasCashLiquidationEnabled,
                viaContext: true,
              },
            ].map((opt) => (
              <li key={opt.id} className="px-6 py-4 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-medium text-gray-900">{opt.label}</p>
                  <p className="text-sm text-gray-500">{opt.description}</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={opt.enabled}
                  onClick={() => {
                    if (opt.viaContext) {
                      runToggle(setVentasCashLiquidationEnabled(!opt.enabled));
                      return;
                    }
                    runVentasOptionToggle(opt.key, !opt.enabled, opt.setter);
                  }}
                  disabled={!loaded}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                    opt.enabled ? 'bg-teal-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                      opt.enabled ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {produccionEnabled && (
        <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-medium text-gray-800">Opciones de Producción</h2>
            <p className="text-sm text-gray-500 mt-1">
              Comportamiento al sacar a producción guías de producto terminado.
            </p>
          </div>
          <ul className="divide-y divide-gray-200">
            <li className="px-6 py-4 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="font-medium text-gray-900">
                  Fabricar productos en proceso desde producto terminado (valor por defecto)
                </p>
                <p className="text-sm text-gray-500">
                  Valor inicial para recetas PEP nuevas. Cada receta «Producto en proceso» puede elegir
                  subreceta automática o guía PEP independiente en su ficha.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={produccionFabricarPepDesdePtEnabled}
                onClick={() =>
                  runToggle(setProduccionFabricarPepDesdePtEnabled(!produccionFabricarPepDesdePtEnabled))
                }
                disabled={!loaded}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                  produccionFabricarPepDesdePtEnabled ? 'bg-teal-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                    produccionFabricarPepDesdePtEnabled ? 'translate-x-5' : 'translate-x-1'
                  }`}
                />
              </button>
            </li>
            <li className="px-6 py-4 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="font-medium text-gray-900">Líneas de producción</p>
                <p className="text-sm text-gray-500">
                  Permite asignar cada receta a una línea y filtrar las guías de producción por línea.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={produccionLineasEnabled}
                onClick={() => runToggle(setProduccionLineasEnabled(!produccionLineasEnabled))}
                disabled={!loaded}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                  produccionLineasEnabled ? 'bg-teal-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                    produccionLineasEnabled ? 'translate-x-5' : 'translate-x-1'
                  }`}
                />
              </button>
            </li>
          </ul>
        </div>
      )}

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, isOpen: false }))}
      />
    </div>
  );
};

export default ModuleSettings;
